import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@v2/design-system';
import type { OrderProductCard } from '../types';
import { formatBrl } from '../utils';
import DealFulfillmentLineControls from './DealFulfillmentLineControls';
import DealProductDetailModal from './DealProductDetailModal';
import ProductThumbnail from '@v2/features/products/ProductThumbnail';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import { DOT_STYLE, dotInlineStyle, isNoCustomization } from '@v2/features/colors/ColorDot';

type Props = {
    product: OrderProductCard;
    dealId: number;
    canRequestStock: boolean;
};

/**
 * Card de produto do deal — layout alinhado ao shop Isomorphic
 * (Product Card Modern: thumbnail no topo + título/preço/cores abaixo).
 */
export default function PloomesDealProductCard({ product, dealId, canRequestStock }: Props) {
    const href = product.local_product_id ? `/products/${product.local_product_id}` : null;
    const mode = product.fulfillment_mode ?? 'import';
    const swatches = product.color_swatches.filter((s) => s.name);
    // Cor vendida que o catálogo do produto não aceita — o vendedor corrige antes da PO.
    const notAccepted = (product.color_check ?? []).filter((c) => !c.accepted);
    const [detailOpen, setDetailOpen] = useState(false);
    const { t } = useI18n();

    const title = href ? (
        <Link
            to={href}
            className="line-clamp-2 text-sm font-semibold text-gray-900 hover:text-primary dark:text-gray-100"
        >
            {product.product_name}
        </Link>
    ) : (
        <p className="line-clamp-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            {product.product_name}
        </p>
    );

    return (
        <div
            className={cn(
                'group flex h-full min-w-0 flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition hover:shadow-md dark:bg-gray-800',
                mode === 'stock' && 'border-emerald-200 hover:border-emerald-300 dark:border-emerald-500/30',
                mode === 'import' && 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600',
                mode === 'split' && 'border-amber-200 hover:border-amber-300 dark:border-amber-500/30',
                mode === 'pending_stock' && 'border-amber-300 hover:border-amber-400 dark:border-amber-500/40',
            )}
        >
            {/* Thumbnail — padrão shop / ProductGridCard */}
            <div className="relative shrink-0 border-b border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-900">
                <button
                    type="button"
                    onClick={() => setDetailOpen(true)}
                    title={t('v2.ploomes_deals.product_modal.open_hint')}
                    className="block w-full cursor-zoom-in transition hover:opacity-90"
                >
                    <ProductThumbnail src={product.image_url} alt={product.product_name} size="grid" />
                </button>

                <span className="pointer-events-none absolute left-2 top-2 z-10 flex flex-col gap-1">
                    {product.stock_available > 0 ? (
                        <Badge color="success" variant="flat" rounded="pill" size="sm">
                            {product.stock_available} estoque
                        </Badge>
                    ) : (
                        <Badge color="secondary" variant="flat" rounded="pill" size="sm">
                            Sem estoque
                        </Badge>
                    )}
                    {product.bonus && (
                        <Badge color="warning" variant="flat" rounded="pill" size="sm">
                            Bônus
                        </Badge>
                    )}
                </span>

                {product.supplier_label && (
                    <span className="absolute right-2 top-2 z-10 inline-flex max-w-[45%] items-center gap-1 rounded-full border border-gray-200/90 bg-white/95 px-1.5 py-0.5 shadow-sm dark:border-zinc-600 dark:bg-zinc-800/95">
                        {product.supplier_logo_url ? (
                            <img
                                src={product.supplier_logo_url}
                                alt=""
                                className="h-3.5 w-3.5 rounded-full object-cover"
                            />
                        ) : (
                            <span
                                className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-bold text-white"
                                style={{ backgroundColor: product.supplier_thumbnail_color ?? '#6b7280' }}
                            >
                                {product.supplier_abbreviation?.charAt(0) ?? '?'}
                            </span>
                        )}
                        <span className="truncate text-[9px] font-semibold text-gray-600 dark:text-zinc-300">
                            {product.supplier_label}
                        </span>
                    </span>
                )}
            </div>

            <div className="flex flex-1 flex-col gap-2 p-3 sm:p-4">
                <div>
                    {title}
                    {product.product_code && (
                        <p className="mt-1 font-mono text-[11px] text-gray-500 dark:text-gray-400">
                            {product.product_code}
                        </p>
                    )}
                </div>

                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-gray-100">
                        {formatBrl(product.total)}
                    </p>
                    {product.discount > 0 && (
                        <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                            −{Math.round(product.discount)}%
                        </span>
                    )}
                </div>
                <p className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                    {Math.round(product.quantity)}× {formatBrl(product.unit_price)}
                    {product.is_high_turnover && (
                        <span
                                className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800 ring-1 ring-sky-200 dark:bg-sky-900/40 dark:text-sky-200"
                                title="Produto de alto giro: reservar trava estoque da venda pronta-entrega — valide com o comercial."
                            >
                                Alto giro
                            </span>
                    )}
                </p>

                {swatches.length > 0 && (
                    <div className="flex items-center gap-1.5">
                        <div className="flex -space-x-1">
                            {swatches.map((swatch) => {
                                // Mesmos estados da PO/PI: padrão · sem customização · fora do padrão.
                                const state = isNoCustomization(swatch.name) ? 'none' : swatch.hex ? 'standard' : 'off';
                                const label =
                                    state === 'none'
                                        ? `${swatch.label}: sem customização`
                                        : state === 'off'
                                          ? `${swatch.label}: ${swatch.name} (fora do padrão)`
                                          : `${swatch.label}: ${swatch.name}`;
                                return (
                                    <span
                                        key={swatch.slot}
                                        title={label}
                                        aria-label={label}
                                        role="img"
                                        className={`inline-flex size-5 rounded-full border-2 border-white transition-transform duration-150 hover:z-10 hover:scale-125 dark:border-gray-800 ${DOT_STYLE[state]}`}
                                        style={dotInlineStyle(state, swatch.hex)}
                                    />
                                );
                            })}
                        </div>
                        {notAccepted.length > 0 && (
                            <span
                                className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-300 dark:bg-amber-900/40 dark:text-amber-200"
                                title={notAccepted
                                    .map((c) => `${c.label}: ${c.sold} não está entre as cores aceitas (${c.accepted_colors.join(', ')})`)
                                    .join('\n')}
                            >
                                Cor não aceita pelo produto
                            </span>
                        )}
                    </div>
                )}

                <div className="mt-auto pt-1">
                    <DealFulfillmentLineControls
                        dealId={dealId}
                        product={product}
                        canRequestStock={canRequestStock}
                    />
                </div>
            </div>
            {detailOpen && <DealProductDetailModal product={product} onClose={() => setDetailOpen(false)} />}
        </div>
    );
}
